import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { updateCompany } from '../../data/thunks/companyThunk';
import { CompanyUpdateDTO } from '../../data/models/company';
import * as GoogleStorageService from '../../services/google-storage.service';
import { processImageForUpload } from '../../utils/image-processing';
import Paper, { PaperHeader } from '../common/layout/Paper';

const DropZone = styled(Box)(({ theme }) => ({
	border: `2px dashed ${theme.palette.divider}`,
	borderRadius: theme.shape.borderRadius,
	padding: theme.spacing(3),
	textAlign: 'center',
	cursor: 'pointer',
	'&:hover': {
		borderColor: theme.palette.primary.main,
	},
}));

const LogoPreview = styled('img')(({ theme }) => ({
	maxWidth: '200px',
	maxHeight: '200px',
	objectFit: 'contain',
	borderRadius: theme.shape.borderRadius,
}));

interface CompanyLogoUploadProps {
	logoUrl?: string;
}

export default function CompanyLogo({ logoUrl }: CompanyLogoUploadProps) {
	const [isUploading, setIsUploading] = useState(false);
	const dispatch = useDispatch<AppDispatch>();
	const companyId = useSelector(
		(state: RootState) => state.companyState?.company?.id
	);

	const onDrop = useCallback(
		async (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (!file) return;

			setIsUploading(true);
			try {
				// Process the image (convert HEIC/HEIF and resize if needed)
				const { processedFile } = await processImageForUpload(file);

				// Upload file to backend
				const path = `${companyId}/logo/${processedFile.name}`;
				const response = await GoogleStorageService.uploadFile(
					processedFile,
					path
				);

				// Update company with new logo URL
				const updateData: CompanyUpdateDTO = {
					logoUrl: response.url,
				};
				await dispatch(updateCompany(updateData));
			} catch (error) {
				console.error('Error uploading logo:', error);
			} finally {
				setIsUploading(false);
			}
		},
		[dispatch]
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
		},
		maxFiles: 1,
	});

	return (
		<Paper gradientBorder>
			<PaperHeader>Logo</PaperHeader>
			<Box
				{...getRootProps()}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 2,
					padding: 2,
				}}
			>
				<input {...getInputProps()} />
				{isUploading ? (
					<CircularProgress />
				) : logoUrl ? (
					<>
						<LogoPreview src={logoUrl} alt="Company Logo" />
						<Typography variant="body2" color="textSecondary">
							Click or drag to change logo
						</Typography>
					</>
				) : (
					<DropZone>
						{isDragActive ? (
							<Typography>Drop the logo here ...</Typography>
						) : (
							<Typography>
								Drag and drop a logo here, or click to select a
								file
							</Typography>
						)}
					</DropZone>
				)}
			</Box>
		</Paper>
	);
}
